import { IconCheck } from '@assets/icons/components/IconCheck';
import { IconPencil } from '@assets/icons/components/IconPencil';
import { IconUserRight } from '@assets/icons/components/IconUserRight';
import { IS_TEACHER } from '@constants';
import { IStudentDto } from '@dto';
import loadable from '@loadable/component';
import { useGameContext } from 'context/GameContext';
import AppSignalR, { SIGNALR_EVENTS } from 'core/signalR';
import { useBoolean } from 'hooks/core/useBoolean';
import React, { memo, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import trans from 'translation';
import { KColors, KContainer, KInput, KLabel } from 'uikit';
import { UIUtils } from 'utils';

import { IFormData } from './helpers';

const MoveStudent = loadable(() => import('./components/MoveStudent'));

interface IProps {
  item: IStudentDto;
  index: number;
  isTeacher?: boolean;
}

const ObservationItem = ({ item, index }: IProps) => {
  const { roomInfo } = useGameContext();

  const methods = useFormContext<IFormData>();

  const [teams, userId, users, numberOfStudents, observations] = useWatch({
    control: methods.control,
    name: [
      'teams',
      `observations.${index}.id`,
      'users',
      'numberOfStudents',
      'observations'
    ]
  });

  const [name, setName] = useState<string>(item.username ?? '');

  const { value: isView, toggle } = useBoolean(true);

  const onSubmitMoveStudent = useCallback(
    (_teamId?: string) => {
      if (!_teamId) {
        return;
      }

      if (_teamId) {
        const obs = observations.find(i => i.id === userId);
        if (obs) {
          obs.teamId = _teamId;
          AppSignalR.emit(SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS, {
            ...roomInfo,
            users: (roomInfo?.users ?? []).concat([obs]),
            observations: (roomInfo?.observations ?? []).filter(
              i => i.id !== userId
            )
          });
        }
      }
    },
    [observations, roomInfo, userId]
  );

  const onMoveStudent = useCallback(() => {
    UIUtils.popup.open({
      withXIcon: true,
      headIcon: {
        jsx: (
          <IconUserRight stroke={KColors.primary.normal} fill={KColors.white} />
        )
      },
      maxWidth: 'xs',
      title: trans('common.move_to_team'),
      content: (
        <MoveStudent
          teams={teams.filter(
            t => users.filter(u => t.id === u.teamId).length < numberOfStudents
          )}
          onSubmit={onSubmitMoveStudent}
        />
      )
    });
  }, [numberOfStudents, onSubmitMoveStudent, teams, users]);

  return (
    <KContainer.View row alignItems marginT="1rem" gap="0.75rem">
      <KContainer.View
        br="round"
        size={50}
        brW={1}
        brC={'#ffc746'}
        padding="0.25rem"
        center
      >
        <KContainer.View
          br="round"
          width="100%"
          height="100%"
          background="#fbe8a8"
        />
      </KContainer.View>

      <KContainer.View flex>
        {isView ? (
          <KLabel.Text color={KColors.highlight.normal} typo="TextMdNormal">
            {name}
          </KLabel.Text>
        ) : (
          <KInput.TextField
            name="studentName"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        )}
      </KContainer.View>

      {IS_TEACHER && (
        <>
          <KContainer.Touchable
            br="round"
            size={36}
            background={isView ? KColors.secondary.normal : KColors.white}
            onPress={() => {
              if (!isView) {
                methods.setValue(`observations.${index}.username`, name);
                AppSignalR.emit(SIGNALR_EVENTS.UPDATE_ROOM_SETTINGS, {
                  ...roomInfo,
                  observations: (roomInfo?.observations ?? []).map(i => {
                    if (i.id === userId) {
                      i.username = name;
                    }
                    return i;
                  })
                });
              }
              toggle();
            }}
            center
          >
            {isView ? (
              <IconPencil stroke={KColors.primary.normal} />
            ) : (
              <IconCheck stroke={KColors.primary.normal} fill={KColors.white} />
            )}
          </KContainer.Touchable>

          <KContainer.Touchable
            br="round"
            size={36}
            background={KColors.secondary.normal}
            onPress={() => onMoveStudent()}
            center
          >
            <IconUserRight
              stroke={KColors.primary.normal}
              fill={KColors.white}
            />
          </KContainer.Touchable>
        </>
      )}
    </KContainer.View>
  );
};

export default memo(ObservationItem);
